import { AdminLayout } from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Pencil, Trash2, Loader2 } from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  adminService,
  ShoppingOrderingAddress,
} from "../../services/adminService";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const defaultForm = {
  country_code: "",
  favor_of: "",
  check_mailing_address: "",
  email_ids: "",
};

const ManageAddress = () => {
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] =
    useState<ShoppingOrderingAddress | null>(null);
  const [addresses, setAddresses] = useState<ShoppingOrderingAddress[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pagination, setPagination] = useState({
    count: 0,
    page: 1,
    pageSize: 10,
  });
  const [formData, setFormData] = useState(defaultForm);
  const [deleteTarget, setDeleteTarget] =
    useState<ShoppingOrderingAddress | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const fetchAddresses = useCallback(
    async (page = 1) => {
      try {
        setLoading(true);
        const data = await adminService.getShoppingOrderingAddresses({
          page,
          page_size: pagination.pageSize,
          ordering: "country_code,id",
        });
        setAddresses(data?.results ?? []);
        setPagination(prev => ({
          ...prev,
          count: data?.count ?? 0,
          page,
        }));
      } catch (error: unknown) {
        console.error("Error fetching addresses:", error);
        toast.error("Failed to load addresses. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [pagination.pageSize]
  );

  useEffect(() => {
    fetchAddresses();
  }, [fetchAddresses]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleOpenAdd = () => {
    setFormData(defaultForm);
    setAddDialogOpen(true);
  };

  const handleOpenEdit = (item: ShoppingOrderingAddress) => {
    setSelectedItem(item);
    setFormData({
      country_code: item.country_code ?? "",
      favor_of: item.favor_of ?? "",
      check_mailing_address: item.check_mailing_address ?? "",
      email_ids: item.email_ids ?? "",
    });
    setEditDialogOpen(true);
  };

  const handleSaveNew = async () => {
    try {
      setSaving(true);
      await adminService.createShoppingOrderingAddress({
        country_code: formData.country_code || null,
        favor_of: formData.favor_of || null,
        check_mailing_address: formData.check_mailing_address || null,
        email_ids: formData.email_ids || null,
      });
      toast.success("Address added successfully");
      setAddDialogOpen(false);
      setFormData(defaultForm);
      fetchAddresses(pagination.page);
    } catch (error: unknown) {
      const msg =
        error && typeof error === "object" && "response" in error
          ? (error as { response?: { data?: { detail?: string } } }).response
              ?.data?.detail
          : null;
      toast.error(msg ?? "Failed to add address");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedItem) {
      return;
    }
    try {
      setSaving(true);
      await adminService.updateShoppingOrderingAddress(selectedItem.id, {
        country_code: formData.country_code || null,
        favor_of: formData.favor_of || null,
        check_mailing_address: formData.check_mailing_address || null,
        email_ids: formData.email_ids || null,
      });
      toast.success("Address updated successfully");
      setEditDialogOpen(false);
      setSelectedItem(null);
      fetchAddresses(pagination.page);
    } catch (error: unknown) {
      const msg =
        error && typeof error === "object" && "response" in error
          ? (error as { response?: { data?: { detail?: string } } }).response
              ?.data?.detail
          : null;
      toast.error(msg ?? "Failed to update address");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (item: ShoppingOrderingAddress) => {
    setDeleteTarget(item);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) {
      return;
    }
    try {
      await adminService.deleteShoppingOrderingAddress(deleteTarget.id);
      toast.success("Address deleted successfully");
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      fetchAddresses(pagination.page);
    } catch (error: unknown) {
      const msg =
        error && typeof error === "object" && "response" in error
          ? (error as { response?: { data?: { detail?: string } } }).response
              ?.data?.detail
          : null;
      toast.error(msg ?? "Failed to delete address");
    }
  };

  const totalPages = Math.ceil(pagination.count / pagination.pageSize) || 1;

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-3xl font-bold text-foreground">Manage Address</h2>
          <Button
            className="bg-[#654321] text-white hover:bg-[#543210]"
            onClick={handleOpenAdd}
          >
            Add Address
          </Button>
        </div>

        <div className="overflow-hidden rounded-lg border border-border bg-card">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase">
                  ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase">
                  Country Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase">
                  Favor Of
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase">
                  Check Mailing Address (postal contact)
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase">
                  Email IDs (To:)
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-8 text-center text-muted-foreground"
                  >
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" /> Loading
                      addresses...
                    </span>
                  </td>
                </tr>
              ) : addresses.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-8 text-center text-muted-foreground"
                  >
                    No addresses found. Add one or migrate data from the old DB.
                  </td>
                </tr>
              ) : (
                addresses.map(item => (
                  <tr key={item.id} className="hover:bg-muted/30">
                    <td className="px-6 py-3 text-sm">{item.id}</td>
                    <td className="px-6 py-3 text-sm">
                      {item.country_code ?? "—"}
                    </td>
                    <td
                      className="max-w-[120px] truncate px-6 py-3 text-sm"
                      title={item.favor_of ?? undefined}
                    >
                      {item.favor_of ?? "—"}
                    </td>
                    <td
                      className="max-w-xs truncate px-6 py-3 text-sm"
                      title={item.check_mailing_address ?? undefined}
                    >
                      {item.check_mailing_address ?? "—"}
                    </td>
                    <td
                      className="max-w-xs truncate px-6 py-3 text-sm"
                      title={item.email_ids ?? undefined}
                    >
                      {item.email_ids ?? "—"}
                    </td>
                    <td className="px-6 py-3">
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          className="h-8 w-8 rounded-full bg-primary p-0 text-primary-foreground hover:bg-primary/90"
                          onClick={() => handleOpenEdit(item)}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          className="h-8 w-8 rounded-full bg-destructive p-0 text-destructive-foreground hover:bg-destructive/90"
                          onClick={() => handleDeleteClick(item)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pagination.count > 0 && (
          <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
            <div className="text-sm text-muted-foreground">
              Showing{" "}
              <span className="font-medium text-foreground">
                {(pagination.page - 1) * pagination.pageSize + 1}
              </span>{" "}
              to{" "}
              <span className="font-medium text-foreground">
                {Math.min(
                  pagination.page * pagination.pageSize,
                  pagination.count
                )}
              </span>{" "}
              of{" "}
              <span className="font-medium text-foreground">
                {pagination.count}
              </span>{" "}
              results
            </div>
            <div className="flex flex-wrap justify-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchAddresses(1)}
                disabled={pagination.page === 1 || loading}
              >
                « First
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchAddresses(pagination.page - 1)}
                disabled={pagination.page === 1 || loading}
              >
                ‹ Back
              </Button>
              <div className="flex items-center rounded-md border bg-muted/50 px-4 text-sm font-medium">
                Page {pagination.page} of {totalPages}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchAddresses(pagination.page + 1)}
                disabled={pagination.page >= totalPages || loading}
              >
                Next ›
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchAddresses(totalPages)}
                disabled={pagination.page >= totalPages || loading}
              >
                Last »
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Add Address Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Address</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label required>Country Code</Label>
              <Select
                value={formData.country_code || undefined}
                onValueChange={value =>
                  setFormData(prev => ({ ...prev, country_code: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="IND">India</SelectItem>
                  <SelectItem value="USA">USA</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label required>Favor Of</Label>
              <Input
                name="favor_of"
                value={formData.favor_of}
                onChange={handleInputChange}
                placeholder="In favor of / payee"
              />
            </div>
            <div>
              <Label required>Check Mailing Address (postal contact)</Label>
              <Textarea
                name="check_mailing_address"
                value={formData.check_mailing_address}
                onChange={handleInputChange}
                placeholder="Postal/contact address for orders"
                rows={3}
              />
            </div>
            <div>
              <Label required>Email IDs (To: for order emails)</Label>
              <Textarea
                name="email_ids"
                value={formData.email_ids}
                onChange={handleInputChange}
                placeholder="Comma-separated emails"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveNew} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving...
                </>
              ) : (
                "Add"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Address Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Update Address</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label required>Country Code</Label>
              <Select
                value={formData.country_code || undefined}
                onValueChange={value =>
                  setFormData(prev => ({ ...prev, country_code: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="IND">India</SelectItem>
                  <SelectItem value="USA">USA</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label required>Favor Of</Label>
              <Input
                name="favor_of"
                value={formData.favor_of}
                onChange={handleInputChange}
                placeholder="In favor of / payee"
              />
            </div>
            <div>
              <Label required>Check Mailing Address (postal contact)</Label>
              <Textarea
                name="check_mailing_address"
                value={formData.check_mailing_address}
                onChange={handleInputChange}
                placeholder="Postal/contact address for orders"
                rows={3}
              />
            </div>
            <div>
              <Label required>Email IDs (To: for order emails)</Label>
              <Textarea
                name="email_ids"
                value={formData.email_ids}
                onChange={handleInputChange}
                placeholder="Comma-separated emails"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              className="bg-green-600 text-white hover:bg-green-700"
              onClick={handleUpdate}
              disabled={saving}
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving...
                </>
              ) : (
                "Update"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete address?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove this ordering/email address. This action cannot
              be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteTarget(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDeleteConfirm}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
};

export default ManageAddress;
